import { Component } from '@theme/component';
import { ThemeEvents, QuantitySelectorUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd, yieldToMainThread } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'addToCartButton', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  /** @type {MutationObserver | null} */
  #needsSizeObserver = null;

  /**
   * State of the placeholder variant, saved while the bar shows "Select size" so it can be put back
   * if the prompt ends without a re-render.
   * @type {{ available: string | undefined, disabled: boolean, label: string, variantTitle: string } | null}
   */
  #savedState = null;

  /** @type {MutationObserver | null} */
  #mainButtonObserver = null;

  /** @type {string} The sticky button's own label, put back when a variant stops being pre-order */
  #labelBeforePreorder = '';

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#watchSizeSelection();
    this.#watchMainButton();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#needsSizeObserver?.disconnect();
    this.#mainButtonObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
  }

  // Public action handlers
  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    // No size chosen yet: send the shopper to the size picker instead of adding anything.
    if (this.#needsSize()) {
      this.#scrollToSizePicker();
      return;
    }

    if (!this.#targetAddToCartButton) return;
    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();

    // Aisle: same feedback as the main Add to cart button (its checkmark "added" state), without
    // the image flying to the cart. Mirrors AddToCartComponent.animateAddToCart in product-form.js.
    const { addToCartButton } = this.refs;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);
    addToCartButton.dataset.added = 'true';

    // onAnimationEnd can trigger a style recalculation, so yield to the main thread first
    await yieldToMainThread();
    await onAnimationEnd(addToCartButton);
    this.#resetTimeout = setTimeout(() => {
      addToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) return;

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) return;

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) return;

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;

    // The morph replaced the button and variant title with fresh server markup
    this.#savedState = null;

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }
    this.#watchMainButton();

    if (variant == null) {
      this.#handleVariantUnavailable();
    }
    // Restore the current quantity display if needed
    this.#updateButtonText();

    // Re-apply the "Select size" prompt if a size still hasn't been chosen
    this.#syncSizePrompt();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const color = this.#getSelectedColor();
    const size = this.#getSelectedSize();
    if (!color && !size) return;
    this.#renderVariantLine(color, size);
  };

  /**
   * Handles cart add complete (success or error) - resets puppet flag
   * @param {CustomEvent} _event - The cart event (unused)
   */
  #handleCartAddComplete = (_event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;
    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // "Select size" prompt (PDP redesign: no size is pre-selected)
  /**
   * The redesigned PDP's purchase wrapper. It sets `data-needs-size` while no size has been chosen.
   * @returns {HTMLElement | null}
   */
  #getPurchase() {
    const purchase = this.closest('.shopify-section')?.querySelector('pdp-purchase-component');
    return purchase instanceof HTMLElement ? purchase : null;
  }

  /**
   * @returns {boolean} True when the shopper still has to pick a size
   */
  #needsSize() {
    return this.#getPurchase()?.hasAttribute('data-needs-size') ?? false;
  }

  /**
   * Watches the purchase wrapper so the bar switches between "Select size" and add to cart
   * as soon as a size is chosen.
   */
  #watchSizeSelection() {
    const purchase = this.#getPurchase();
    if (!purchase) return;

    this.#needsSizeObserver = new MutationObserver(() => this.#syncSizePrompt());
    this.#needsSizeObserver.observe(purchase, { attributes: true, attributeFilter: ['data-needs-size'] });
    this.#syncSizePrompt();
  }

  /**
   * Shows "Select size" (and hides the placeholder size) while no size is chosen; restores the
   * normal add to cart state once one is.
   */
  #syncSizePrompt() {
    const { addToCartButton } = this.refs;
    const label = addToCartButton.querySelector('.add-to-cart-text__content > span');
    const textContent = addToCartButton.querySelector('.add-to-cart-text__content');
    const icon = addToCartButton.querySelector('.add-to-cart-icon');
    const variantTitle = this.querySelector('.sticky-add-to-cart__variant');

    if (this.#needsSize()) {
      if (!this.#savedState) {
        this.#savedState = {
          available: this.dataset.variantAvailable,
          disabled: addToCartButton.disabled,
          label: label?.textContent ?? '',
          variantTitle: variantTitle?.innerHTML ?? '',
        };
      }

      const selectSizeLabel = this.#getPurchase()?.dataset.selectSizeLabel || 'Select size';

      this.toggleAttribute('data-needs-size', true);
      // Keep the bar visible even if the placeholder variant happens to be sold out
      this.dataset.variantAvailable = 'true';
      addToCartButton.disabled = false;
      addToCartButton.setAttribute('aria-label', selectSizeLabel);
      if (label) label.textContent = selectSizeLabel;
      // On mobile the theme shows only an icon; show the words instead
      if (textContent instanceof HTMLElement) textContent.style.display = 'initial';
      if (icon instanceof HTMLElement) icon.style.display = 'none';
      addToCartButton.style.width = 'auto';
      if (variantTitle) this.#renderVariantLine(this.#getSelectedColor(), '');
    } else if (this.#savedState) {
      const saved = this.#savedState;
      this.#savedState = null;

      this.removeAttribute('data-needs-size');
      this.dataset.variantAvailable = saved.available ?? '';
      addToCartButton.disabled = saved.disabled;
      addToCartButton.removeAttribute('aria-label');
      if (label) label.textContent = saved.label;
      if (textContent instanceof HTMLElement) textContent.style.removeProperty('display');
      if (icon instanceof HTMLElement) icon.style.removeProperty('display');
      addToCartButton.style.removeProperty('width');
      if (variantTitle) variantTitle.innerHTML = saved.variantTitle;
    } else {
      this.removeAttribute('data-needs-size');
    }

    this.#updateButtonText();
    this.#syncPreorder();
  }

  // Pre-order (NotifyMe): the sticky button copies the main button's pre-order label
  /**
   * Watches the main add to cart form so the sticky button follows NotifyMe's pre-order label,
   * which can change per variant after the page loads.
   */
  #watchMainButton() {
    this.#mainButtonObserver?.disconnect();
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#mainButtonObserver = new MutationObserver(() => this.#syncPreorder());
    this.#mainButtonObserver.observe(productForm, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class'],
    });
    this.#syncPreorder();
  }

  /**
   * The main button's label when it's a pre-order (e.g. "Pre-order now"), otherwise ''.
   * NotifyMe hides the real label and draws its own with ::after, so read that first.
   * @returns {string}
   */
  #getMainPreorderLabel() {
    const content = this.#getProductForm()?.querySelector('[ref="addToCartButton"] .add-to-cart-text__content');
    if (!(content instanceof HTMLElement)) return '';

    const after = getComputedStyle(content, '::after').content;
    const drawn = after && after !== 'none' && after !== 'normal' ? after.replace(/^["']|["']$/g, '') : '';
    const text = drawn || (content.textContent ?? '').replace(/\s+/g, ' ').trim();
    return /pre-?order/i.test(text) ? text : '';
  }

  /**
   * Shows the pre-order label on the sticky button for pre-order variants. Adding still works the
   * same way: the sticky button presses the main button, which already carries NotifyMe's
   * pre-order selling plan. "Select a size" takes priority while no size is chosen.
   */
  #syncPreorder() {
    if (this.#needsSize()) return;

    const label = this.refs.addToCartButton.querySelector('.add-to-cart-text__content > span');
    const preorderLabel = this.#getMainPreorderLabel();

    if (preorderLabel) {
      if (!this.hasAttribute('data-preorder')) this.#labelBeforePreorder = label?.textContent ?? '';
      this.toggleAttribute('data-preorder', true);
      if (label && label.textContent !== preorderLabel) label.textContent = preorderLabel;
    } else if (this.hasAttribute('data-preorder')) {
      this.removeAttribute('data-preorder');
      if (label && this.#labelBeforePreorder) label.textContent = this.#labelBeforePreorder;
    }
  }

  /**
   * The chosen colour (any option except size, absorbency and pack size, which the sticky bar hides).
   * @returns {string}
   */
  #getSelectedColor() {
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!variantPicker) return '';

    return Array.from(variantPicker.querySelectorAll('input:checked'))
      .filter(
        (input) =>
          input instanceof HTMLInputElement &&
          !input.closest('[data-option-kind="size"], [data-option-kind="pack"], [data-option-kind="absorbency"]')
      )
      .map((input) => /** @type {HTMLInputElement} */ (input).value)
      .filter((value) => value !== '')
      .join(' / ');
  }

  /**
   * The chosen size, or '' while no size is chosen.
   * @returns {string}
   */
  #getSelectedSize() {
    if (this.#needsSize()) return '';
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    const sizeInput = variantPicker?.querySelector('[data-option-kind="size"] input:checked');
    return sizeInput instanceof HTMLInputElement ? sizeInput.value : '';
  }

  /**
   * Writes the colour / size line with the same markup as the Liquid, so a long colour shortens
   * with "…" while the size stays visible.
   * @param {string} color
   * @param {string} size
   */
  #renderVariantLine(color, size) {
    const variantTitle = this.querySelector('.sticky-add-to-cart__variant');
    if (!variantTitle) return;

    const parts = [];
    if (color) {
      parts.push(Object.assign(document.createElement('span'), { className: 'sticky-add-to-cart__variant-color', textContent: color }));
    }
    if (size) {
      parts.push(
        Object.assign(document.createElement('span'), {
          className: 'sticky-add-to-cart__variant-size',
          textContent: color ? `\u00a0/\u00a0${size}` : size,
        })
      );
    }
    variantTitle.replaceChildren(...parts);
  }

  /**
   * Scrolls the size picker into view and moves keyboard focus to it.
   */
  #scrollToSizePicker() {
    const sizeOption = this.#getPurchase()?.querySelector('[data-option-kind="size"]');
    if (!(sizeOption instanceof HTMLElement)) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    sizeOption.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });

    const firstSize = sizeOption.querySelector('input:not([disabled])');
    if (firstSize instanceof HTMLElement) firstSize.focus({ preventScroll: true });
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;

    const available = !addToCartButton.disabled && !this.hasAttribute('data-needs-size');

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}